// SdkLoginView.tsx
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    Form,
    Input,
    Button,
    Card,
    Typography,
    Row,
    Col,
    Alert,
    Space,
    message,
} from 'antd';
import {
    MailOutlined,
    LockOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import { Link, useNavigate } from 'react-router-dom';
import { ROOT } from '../services/Api';
import { sdkMgmt } from '../server/SdkMgmt';
import { Page } from '../services/Page';

const { Title, Text } = Typography;

interface LoginFormData {
    email: string;
    password: string;
    remember?: boolean;
}

interface LoginResponse {
    success: boolean;
    message?: string;
    data?: any;
}

// 定义 Props 类型
interface SdkLoginViewProps extends WithTranslation {
    onLoginSuccess?: (data: any) => void;
    onLoginFail?: (error: any) => void;
    loading?: boolean;
}

// 定义 State 类型
interface SdkLoginViewState {
    loading: boolean;
    errorMessage: string;
    showPassword: boolean;
}

class SdkLoginView extends React.Component<SdkLoginViewProps, SdkLoginViewState> {
    formRef = React.createRef<FormInstance>();

    state: SdkLoginViewState = {
        loading: false,
        errorMessage: '',
        showPassword: false,
    };

    // 邮箱校验
    validateEmail = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error('请输入邮箱地址'));
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return Promise.reject(new Error('邮箱格式不正确（如 user@example.com）'));
        }
        return Promise.resolve();
    };

    // 密码校验
    validatePassword = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error('请输入密码'));
        }
        if (value.length < 6) {
            return Promise.reject(new Error('密码长度不能少于6位'));
        }
        if (value.length > 20) {
            return Promise.reject(new Error('密码长度不能超过20位'));
        }
        return Promise.resolve();
    };

    // 提交登录
    handleSubmit = async (values: LoginFormData) => {
        const DEBUG = true;
        const TAG = `SdkLoginView.handleSubmit() - `;
        this.setState({ loading: true, errorMessage: '' });

        try {
            const { email, password, remember } = values;

            if (DEBUG) {
                console.log(TAG, `email:`, email);
                console.log(TAG, `password:`, password);
                console.log(TAG, `remember:`, remember);
            }

            let response = await sdkMgmt.login(email, password);
            DEBUG && console.log(TAG, `response:\n`, response);

            const result: LoginResponse = response;
            DEBUG && console.log(TAG, `result:\n`, result);

            if (result.success) {
                DEBUG && console.log(TAG, `登录成功，显示提示语`);
                message.success({
                    content: '登录成功！欢迎回来',
                    duration: 2,
                });

                // 保存token等信息
                if (result.data?.token) {
                    localStorage.setItem('token', result.data.token);
                }

                // 跳转到 SdkMgmt 页面
                // setTimeout(() => {
                //     window.location.href = `${ROOT}/${Page.sdkMgmt}`;
                // }, 3000)
            } else {
                const errorMsg = result.message || '登录失败，请检查邮箱和密码';
                this.setState({ errorMessage: errorMsg });
                message.error(errorMsg);
                this.props.onLoginFail?.(result);
            }
        } catch (error: any) {
            const errorMsg = error.message || '网络错误，请稍后重试';
            this.setState({ errorMessage: errorMsg });
            message.error(errorMsg);
            this.props.onLoginFail?.(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { t, loading: parentLoading } = this.props;
        const { loading, errorMessage } = this.state;

        return (
            <div className="sdk-login-page">
                <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
                    <Col xs={22} sm={16} md={12} lg={8} xl={6}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                <Title level={3} style={{ marginBottom: 0 }}>
                                    {t('sdk.login.title') || '欢迎登录'}
                                </Title>
                                <Text type="secondary">
                                    {t('sdk.login.subtitle') || '登录您的SDK管理账号'}
                                </Text>
                            </div>

                            {errorMessage && (
                                <Alert
                                    message={errorMessage}
                                    type="error"
                                    showIcon
                                    closable
                                    style={{ marginBottom: 24 }}
                                    onClose={() => this.setState({ errorMessage: '' })}
                                />
                            )}

                            <Form
                                ref={this.formRef}
                                name="login"
                                onFinish={this.handleSubmit}
                                layout="vertical"
                                size="large"
                                requiredMark={false}
                                initialValues={{
                                    remember: true,
                                }}
                            >
                                <Form.Item
                                    name="email"
                                    label="电子邮箱"
                                    rules={[
                                        { required: true, message: '请输入电子邮箱' },
                                        { validator: this.validateEmail },
                                    ]}
                                >
                                    <Input
                                        prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请输入注册邮箱"
                                        autoComplete="email"
                                        size="large"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    label="登录密码"
                                    rules={[
                                        { required: true, message: '请输入登录密码' },
                                        { validator: this.validatePassword },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                        placeholder="请输入登录密码"
                                        autoComplete="current-password"
                                        size="large"
                                    />
                                </Form.Item>

                                <Form.Item style={{ marginBottom: 16 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        loading={loading || parentLoading}
                                        size="large"
                                        style={{
                                            height: 44,
                                            borderRadius: 8,
                                            fontSize: 16,
                                        }}
                                    >
                                        {loading || parentLoading ? '登录中...' : '登 录'}
                                    </Button>
                                </Form.Item>

                                <div style={{ textAlign: 'center' }}>
                                    <Space>
                                        <Text type="secondary">还没有账号？</Text>
                                        <Link to={`${ROOT}/sdkRegister`} style={{ textDecoration: 'none' }}>
                                            <Button type="link" style={{ padding: 0 }}>
                                                立即注册
                                            </Button>
                                        </Link>
                                    </Space>
                                </div>
                            </Form>
                        </Card>

                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                © 2026 SDK Platform. All rights reserved.
                            </Text>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }
}

export const SdkLoginPage = withTranslation()(SdkLoginView);